import {
  Address,
  CheckoutOption,
  Coupon,
  DeliveryMethodNames,
  Id,
  PaymentMethod,
  PickupPoint
} from '@eo-storefronts/eo-core'
import { createSelector, createState } from '~/src/hooks/useEoState'
import { FIRM_SELECTOR } from '~/src/stores/firm'
import { indexDbPersist } from '~/src/helpers/recoilPersist'
import { CART_SUBTOTAL_WITH_DISCOUNTS_SELECTOR } from '~/src/stores/cart'
import CartCoupon from '~/src/types/CartCoupon'

const checkoutFormStateKey = 'checkoutFormState'

export interface CheckoutFormState {
  indicator: string,
  phone: string,
  receiveInvoice: boolean,
  invoiceDetails: {
    address: Address | null,
  },
  delivery_method: {
    initialDeliveryCost: number,
    deliveryCost: number,
    pickupPoint: PickupPoint | null,
    method: DeliveryMethodNames | null,
    address: Address | null,
    asap: boolean,
    date: Date,
    minDate: string | null,
    time: string | null,
    timeslotId: Id | null,
    table_number: string | null,
    table_method: number | null,
    deliveryFreeFromPrice: number | null,
    minAmount: number,
  },
  loyaltyDiscount: number,
  loyaltyProductId: Id | null,
  payment_method: PaymentMethod | null,
  coupon: CartCoupon | null,
  moreInformation: string | null,
  checkoutOptions: CheckoutOption[],
}

export const checkoutFormInitialState = (): CheckoutFormState => (
  {
    indicator: '+32',
    phone: '',
    receiveInvoice: false,
    invoiceDetails: {
      address: null
    },
    delivery_method: {
      initialDeliveryCost: 0,
      deliveryCost: 0,
      method: null,
      pickupPoint: null,
      address: null,
      asap: false,
      date: new Date(),
      minDate: null,
      time: null,
      timeslotId: null,
      table_number: null,
      table_method: null,
      deliveryFreeFromPrice: null,
      minAmount: 0
    },
    loyaltyDiscount: 0,
    loyaltyProductId: null,
    payment_method: null,
    coupon: null,
    moreInformation: null,
    checkoutOptions: []
  }
)

export const CHECKOUT_FORM_STATE = createState<CheckoutFormState>({
  key: checkoutFormStateKey,
  default: checkoutFormInitialState(),
  effects: [
    indexDbPersist('checkoutState')
  ]
})

export const CHECKOUT_BUTTON_LOADING_STATE = createState<boolean>({
  key: 'checkoutButtonLoading',
  default: false
})

export const CHECKOUT_TIME_STATE = createSelector<Date>({
  key: 'checkoutTimeState',
  get: ({ get }) => {
    const checkoutForm = get(CHECKOUT_FORM_STATE)

    if (checkoutForm.delivery_method.time) {
      return new Date(checkoutForm.delivery_method.time)
    }

    return new Date()
  }
})

export const FROZEN_DELIVERY_METHOD_STATE = createState<boolean>({
  key: 'frozenDeliveryMethodState',
  default: false
})

export const GET_TOTAL_AMOUNT_OF_CHECKOUT_OPTIONS_SELECTOR = createSelector<number>({
  key: 'getTotalAmountOfCheckoutOptionsSelector',
  get: ({ get }) => {
    const checkoutForm = get(CHECKOUT_FORM_STATE)

    return checkoutForm.checkoutOptions
      .reduce((accumulator: number, option: CheckoutOption) => accumulator + option.price, 0)
  }
})

export const GET_DELIVERY_COST = createSelector<number>({
  key: 'getDeliveryCost',
  get: ({ get }) => {
    const checkoutForm = get(CHECKOUT_FORM_STATE)
    const totalAmountOfCart = get(CART_SUBTOTAL_WITH_DISCOUNTS_SELECTOR)
    const totalAmountOfCheckoutOptions = get(GET_TOTAL_AMOUNT_OF_CHECKOUT_OPTIONS_SELECTOR)

    if (checkoutForm.delivery_method.method !== DeliveryMethodNames.DELIVERY) {
      return 0
    }

    if (
      checkoutForm.delivery_method.deliveryFreeFromPrice
      && checkoutForm.delivery_method.deliveryFreeFromPrice <= totalAmountOfCart + totalAmountOfCheckoutOptions
    ) {
      return 0
    }

    return checkoutForm.delivery_method.deliveryCost
  }
})

export const CHECKOUT_PICKUP_POINT_SELECTOR = createSelector<PickupPoint | null>({
  key: 'checkoutPickupPointSelector',
  get: ({ get }) => {
    const checkoutForm = get(CHECKOUT_FORM_STATE)

    if (checkoutForm.delivery_method.method === DeliveryMethodNames.PICKUP) {
      return checkoutForm.delivery_method.pickupPoint
    }

    return null
  }
})

export const CHECKOUT_DELIVERY_METHOD_SELECTOR = createSelector<DeliveryMethodNames | null>({
  key: 'checkoutDeliveryMethodSelector',
  get: ({ get })  => {
    const checkoutForm = get(CHECKOUT_FORM_STATE)

    return checkoutForm.delivery_method.method
  }
})

export const CHECKOUT_COUPON_DISCOUNT_PRICE_SELECTOR = createSelector<string>({
  key: 'checkoutCouponDiscountPriceSelector',
  get: ({ get }) => {
    const checkoutForm = get(CHECKOUT_FORM_STATE)
    const firm = get(FIRM_SELECTOR)

    if (!checkoutForm.coupon) {
      return ''
    }

    if (checkoutForm.coupon.type === 'percentage') {
      return `- ${checkoutForm.coupon.value}%`
    }

    return `- ${firm?.settings.currency.symbol} ${checkoutForm.coupon.value?.toFixed(2)}`
  }
})

export const CHECKOUT_PRODUCT_COUPON = createSelector<CartCoupon | undefined>({
  key: 'checkoutProductCoupon',
  get: ({ get }) => {
    const checkoutForm = get(CHECKOUT_FORM_STATE)

    if (!checkoutForm.coupon || !checkoutForm.coupon.chosen_product_id) {
      return undefined
    }

    return checkoutForm.coupon
  }
})

export const CHECKOUT_PRODUCT_COUPON_AMOUNT = createSelector<number>({
  key: 'checkoutProductCouponAmount',
  get: ({ get }) => {
    const coupon = get(CHECKOUT_PRODUCT_COUPON)

    if (!coupon || !coupon.value) {
      return 0
    }

    return coupon.value
  }
})

export const CHECKOUT_PRODUCT_LOYALTY_AMOUNT = createSelector<number>({
  key: 'checkoutProductLoyaltyAmount',
  get: ({ get }) => {
    const checkoutForm = get(CHECKOUT_FORM_STATE)

    if (!checkoutForm.loyaltyProductId) {
      return 0
    }

    return checkoutForm.loyaltyDiscount
  }
})

export const CHECKOUT_PRODUCT_LOYALTY_IN_CART = createSelector<boolean>({
  key: 'checkoutProductLoyaltyInCart',
  get: ({ get }) => {
    const checkoutForm = get(CHECKOUT_FORM_STATE)

    return !!checkoutForm.loyaltyProductId
  }
})

export const COUPON_STATE = createState<Coupon & { code: string } | null>({
  key: 'couponState',
  default: null
})

export const CHECKOUT_OPTIONS_ID_SELECTOR = createSelector<Id[]>({
  key: 'checkoutOptionsIdSelector',
  get: ({ get }) => {
    const checkoutForm = get(CHECKOUT_FORM_STATE)

    return checkoutForm.checkoutOptions.map((option: CheckoutOption) => option.id)
  }
})
